import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import './App.css'; // مطمئن شوید که index.css یا App.css ایمپورت شده است

function App() {
  const [walletAddress, setWalletAddress] = useState(null);
  const [balance, setBalance] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [sendAmount, setSendAmount] = useState('');

  // تابع برای ریست کردن تمام حالت‌ها
  // استفاده از useCallback برای جلوگیری از بازسازی غیرضروری تابع
  const resetWalletState = useCallback(() => {
    setWalletAddress(null);
    setBalance(null);
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(false);
    setRecipientAddress('');
    setSendAmount('');
  }, []); // بدون dependencies، چون فقط state setterها را صدا می زند

  // تابع برای اتصال به MetaMask و دریافت موجودی
  // استفاده از useCallback برای جلوگیری از بازسازی غیرضروری تابع
  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      setErrorMessage("MetaMask is not detected. Please install it.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      // eth_requestAccounts همیشه پنجره انتخاب حساب را باز می کند اگر کاربر قبلاً اجازه نداده باشد
      // یا اگر کاربر در MetaMask "Connect new account" را انتخاب کرده باشد
      const accounts = await provider.send("eth_requestAccounts", []);
      const currentAddress = accounts[0];
      setWalletAddress(currentAddress);

      const signer = await provider.getSigner();
      const rawBalance = await provider.getBalance(signer.address);
      setBalance(ethers.formatEther(rawBalance));

    } catch (error) {
      console.error("Error connecting to MetaMask:", error);
      if (error.code === 4001) { // کاربر درخواست را رد کرده است
        setErrorMessage("Connection rejected by user.");
      } else {
        setErrorMessage("Failed to connect to MetaMask. Please try again.");
      }
      resetWalletState(); // در صورت خطا، حالت را ریست کن
    } finally {
      setIsLoading(false);
    }
  }, [resetWalletState]); // resetWalletState را به عنوان dependency اینجا اضافه کنید

  // تابع برای ارسال توکن
  // استفاده از useCallback برای جلوگیری از بازسازی غیرضروری تابع
  const sendTransaction = useCallback(async () => {
    if (!walletAddress) {
      setErrorMessage("Please connect your wallet first.");
      return;
    }
    if (!recipientAddress || !ethers.isAddress(recipientAddress)) {
      setErrorMessage("Please enter a valid recipient address.");
      return;
    }
    if (!sendAmount || parseFloat(sendAmount) <= 0) {
      setErrorMessage("Please enter a valid amount to send.");
      return;
    }
    if (balance === null || parseFloat(sendAmount) > parseFloat(balance)) {
      setErrorMessage("Insufficient balance.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      // تبدیل مقدار به Wei
      const amountInWei = ethers.parseEther(sendAmount);

      // ایجاد آبجکت تراکنش
      const tx = {
        to: recipientAddress,
        value: amountInWei,
      };

      // ارسال تراکنش و انتظار برای تکمیل آن
      const transactionResponse = await signer.sendTransaction(tx);
      setSuccessMessage(`Transaction sent! Waiting for confirmation... Hash: ${transactionResponse.hash.substring(0, 10)}...`);
      console.log('Transaction response:', transactionResponse);

      // انتظار برای تأیید تراکنش (اختیاری اما توصیه شده)
      const receipt = await transactionResponse.wait();
      if (receipt && receipt.status === 1) { // اطمینان از وجود receipt قبل از دسترسی به status
        setSuccessMessage(`Transaction confirmed successfully! Hash: ${receipt.hash.substring(0, 10)}...`);
        // به روز رسانی موجودی پس از موفقیت
        const rawBalance = await provider.getBalance(signer.address);
        setBalance(ethers.formatEther(rawBalance));
        setRecipientAddress(''); // پاک کردن فیلدها
        setSendAmount('');
      } else {
        setErrorMessage(`Transaction failed on chain. Hash: ${receipt?.hash?.substring(0, 10) || 'N/A'}`); // استفاده از Optional chaining
      }
      
    } catch (error) {
      console.error("Error sending transaction:", error);
      if (error.code === 4001) {
        setErrorMessage("Transaction rejected by user.");
      } else if (error.message.includes("insufficient funds")) {
        setErrorMessage("Insufficient funds for transaction.");
      } else {
        setErrorMessage(`Failed to send transaction: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress, balance, recipientAddress, sendAmount]); // dependencies برای sendTransaction

  // useEffect برای گوش دادن به تغییرات حساب‌ها و شبکه
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          resetWalletState(); // اگر همه حساب‌ها قطع شدند، ریست کن
        } else if (walletAddress && accounts[0].toLowerCase() !== walletAddress.toLowerCase()) {
          // اگر حساب تغییر کرد، دوباره وصل شو و موجودی را بگیر
          // .toLowerCase() برای مقایسه بدون حساسیت به حروف بزرگ و کوچک
          setWalletAddress(accounts[0]); // آدرس جدید را بلافاصله تنظیم کن
          connectWallet(); 
        } else if (!walletAddress && accounts.length > 0) {
          // اگر برنامه disconnected بود ولی MetaMask متصل شد (مثلا کاربر به صورت دستی وصل کرد)
          connectWallet();
        }
      };

      const handleChainChanged = (chainId) => {
        console.log("Network changed to:", chainId);
        // یک تأخیر کوتاه اضافه می‌کنیم تا MetaMask فرصت کند به شبکه جدید سوییچ کند
        setTimeout(() => {
          if (walletAddress) { // اگر متصل بودیم، موجودی را به‌روز کن
            connectWallet();
          } else {
            resetWalletState(); // اگر disconnected بودیم، فقط وضعیت را ریست کن
          }
        }, 300); // 300 میلی‌ثانیه تأخیر
      };

      // در هنگام راه‌اندازی، وضعیت اتصال MetaMask را بررسی می‌کنیم.
      // اگر کاربر قبلاً اجازه داده باشد، MetaMask آدرس انتخاب شده را در selectedAddress نگه می‌دارد.
      // این چک برای زمانی است که کاربر صفحه را رفرش می‌کند و ما می‌خواهیم وضعیت را بازیابی کنیم.
      if (window.ethereum.selectedAddress) {
        connectWallet();
      }

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [walletAddress, resetWalletState, connectWallet]); // تمامی dependencies های مورد نیاز

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-113F67 text-white p-6 font-sans">
      <h1 className="text-5xl font-extrabold text-FDF5AA mb-12 drop-shadow-lg text-center leading-tight">
         Sepolia Wallet
      </h1>

      <div className="bg-34699A p-10 rounded-3xl shadow-2xl w-full max-w-2xl border-2 border-58A0C8 flex flex-col items-center space-y-8 animate-fade-in-up">
        {!walletAddress ? (
          <>
            <p className="text-xl text-gray-200 mb-4 text-center">
              Connect your MetaMask wallet to unlock full functionalities and manage your Sepolia Test ETH.
            </p>
            <button
              onClick={connectWallet}
              className="w-full bg-FDF5AA text-113F67 font-extrabold py-4 px-6 rounded-xl transition duration-300 ease-in-out transform hover:scale-105 shadow-xl flex items-center justify-center space-x-3 text-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-6 w-6 text-113F67" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/MetaMask_Fox.svg/1200px-MetaMask_Fox.svg.png" alt="MetaMask Icon" className="h-7 w-7 inline-block mr-2" />
              )}
              <span>{isLoading ? "Connecting..." : "Connect MetaMask Wallet"}</span>
            </button>
          </>
        ) : (
          <div className="text-center w-full space-y-6">
            <div className="bg-58A0C8 p-6 rounded-2xl shadow-inner border border-FDF5AA animate-slide-down">
                <p className="text-sm text-gray-800 mb-1 font-semibold">Your Wallet Address:</p>
                <p className="text-lg font-mono break-all text-113F67 font-bold">{walletAddress}</p>
            </div>
            
            <div className="bg-58A0C8 p-6 rounded-2xl shadow-inner border border-FDF5AA animate-slide-down delay-100">
                <p className="text-sm text-gray-800 mb-1 font-semibold">Sepolia Test ETH Balance:</p>
                <p className="text-3xl font-extrabold text-113F67">
                  {balance !== null ? `${parseFloat(balance).toFixed(4)} ETH` : "Loading..."}
                </p>
            </div>

            {/* بخش ارسال توکن */}
            <div className="bg-58A0C8 p-6 rounded-2xl shadow-inner border border-FDF5AA animate-slide-down delay-200">
                <h3 className="text-xl font-bold text-113F67 mb-4">Send Sepolia ETH</h3>
                <input
                  type="text"
                  placeholder="Recipient Address (0x...)"
                  className="w-full p-3 rounded-lg bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-FDF5AA mb-3"
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Amount (ETH)"
                  step="0.0001"
                  className="w-full p-3 rounded-lg bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-FDF5AA mb-4"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                />
                <button
                  onClick={sendTransaction}
                  className="w-full bg-113F67 hover:bg-34699A text-white font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-md flex items-center justify-center space-x-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span>Send ETH</span>
                  )}
                </button>
            </div>

            <button
              onClick={resetWalletState} // استفاده از resetWalletState برای قطع اتصال "ظاهری"
              className="w-full bg-113F67 hover:bg-34699A text-white font-bold py-3 px-6 rounded-xl transition duration-300 ease-in-out transform hover:scale-105 shadow-lg mt-4"
            >
              Disconnect Wallet
            </button>
          </div>
        )}

        {errorMessage && (
          <p className="text-FDF5AA bg-113F67 p-3 rounded-lg mt-4 text-center font-medium text-base animate-pulse shadow-md">
            {errorMessage}
          </p>
        )}
        {successMessage && (
          <p className="text-green-400 bg-113F67 p-3 rounded-lg mt-4 text-center font-medium text-base animate-fade-in shadow-md">
            {successMessage}
          </p>
        )}
      </div>

      <p className="mt-10 text-gray-400 text-sm">
        Please ensure your MetaMask is set to the Sepolia Test Network.
      </p>
    </div>
  );
}

export default App;